import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Save, Plus, Trash2, Package, FileSpreadsheet, FileText, LayoutTemplate } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { API } from "@/lib/api";


const UNITS = [
  { value: "un", label: "Unidade (un)" },
  { value: "ml", label: "Mililitro (ml)" },
  { value: "g", label: "Grama (g)" },
  { value: "kg", label: "Quilograma (kg)" },
  { value: "l", label: "Litro (L)" },
  { value: "cm", label: "Centímetro (cm)" },
  { value: "m", label: "Metro (m)" },
  { value: "pct", label: "Pacote (pct)" },
  { value: "cx", label: "Caixa (cx)" },
  { value: "par", label: "Par" },
];

const emptyItem = {
  name: "",
  quantity: 0,
  unit: "un",
  unit_cost: 0,
  product_id: null,
};

// First item is the main product (Valor de Compra)
const emptyMainProduct = {
  name: "",
  quantity: 0,
  unit: "un",
  unit_cost: 0,
  product_id: null,
  is_main_product: true,
};

export default function TechnicalSheetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    items: [{ ...emptyMainProduct }, { ...emptyItem }],
  });
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchTemplates();
    if (isEditing) {
      fetchSheet();
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates/technical-sheets`);
      setTemplates(res.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || "",
        items: template.items.map((item, idx) => ({
          ...item,
          product_id: null,
          is_main_product: idx === 0,
        })),
      });
      toast.success(`Template "${template.name}" aplicado!`);
    }
  };

  const fetchSheet = async () => {
    try {
      const res = await axios.get(`${API}/technical-sheets/${id}`);
      setFormData({
        name: res.data.name,
        description: res.data.description || "",
        items: res.data.items?.length > 0 ? res.data.items : [{ ...emptyMainProduct }, { ...emptyItem }],
      });
    } catch (error) {
      toast.error("Erro ao carregar ficha técnica");
      navigate("/technical-sheets");
    } finally {
      setLoading(false);
    }
  };

  // Calculate main product cost (first item) and supplies cost (remaining items)
  const calculateCosts = useCallback(() => {
    if (formData.items.length === 0) return { mainProduct: 0, supplies: 0, total: 0 };
    
    // First item is main product (Valor de Compra)
    const firstItem = formData.items[0];
    const mainProductCost = (parseFloat(firstItem?.quantity) || 0) * (parseFloat(firstItem?.unit_cost) || 0);
    
    // Remaining items are supplies (Insumos)
    const suppliesCost = formData.items.slice(1).reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      return sum + (qty * cost);
    }, 0);
    
    return {
      mainProduct: mainProductCost,
      supplies: suppliesCost,
      total: mainProductCost + suppliesCost
    };
  }, [formData.items]);

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const handleRemoveItem = (index) => {
    // Cannot remove first item (main product)
    if (index === 0) {
      toast.error("O produto principal não pode ser removido");
      return;
    }
    if (formData.items.length <= 2) {
      toast.error("A ficha técnica precisa ter pelo menos um insumo");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        return { ...item, [field]: value };
      }),
    }));
  };

  const handleProductSelect = (index, productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.map((item, i) => {
          if (i !== index) return item;
          return {
            ...item,
            product_id: product.id,
            name: product.name,
            unit: product.unit,
            unit_cost: product.unit_cost,
          };
        }),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    // Filter valid items (items with name and quantity > 0)
    const validItems = formData.items.filter(
      (item) => item.name && item.name.trim() && parseFloat(item.quantity) > 0
    );

    if (validItems.length < 1) {
      toast.error("Adicione pelo menos um item com nome e quantidade");
      return;
    }

    setSaving(true);
    try {
      // Calculate costs from valid items
      let mainProductCost = 0;
      let suppliesCost = 0;
      
      validItems.forEach((item, idx) => {
        const itemTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
        if (idx === 0) {
          mainProductCost = itemTotal;
        } else {
          suppliesCost += itemTotal;
        }
      });

      const payload = {
        name: formData.name,
        description: formData.description,
        items: validItems.map((item, idx) => ({
          product_id: item.product_id || null,
          name: item.name,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || "un",
          unit_cost: parseFloat(item.unit_cost) || 0,
          is_main_product: idx === 0,
        })),
        main_product_cost: Math.round(mainProductCost * 100) / 100,
        supplies_cost: Math.round(suppliesCost * 100) / 100,
      };

      if (isEditing) {
        await axios.put(`${API}/technical-sheets/${id}`, payload);
        toast.success("Ficha técnica atualizada com sucesso");
      } else {
        await axios.post(`${API}/technical-sheets`, payload);
        toast.success("Ficha técnica criada com sucesso");
      }
      navigate("/technical-sheets");
    } catch (error) {
      toast.error("Erro ao salvar ficha técnica");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const costs = calculateCosts();

  return (
    <div className="space-y-6 animate-slide-in" data-testid="technical-sheet-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/technical-sheets")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {isEditing ? "Editar Ficha Técnica" : "Nova Ficha Técnica"}
          </h1>
          <p className="text-slate-500 mt-1">
            Cadastre os insumos utilizados no procedimento
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  Informações do Procedimento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Procedimento *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Fios de Sustentação"
                    className="editable-field"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição (opcional)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição do procedimento"
                    data-testid="input-description"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Templates - only show when creating */}
            {!isEditing && templates.length > 0 && (
              <Card className="border-2 border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <LayoutTemplate className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <CardTitle className="text-base text-amber-800 dark:text-amber-300" style={{ fontFamily: 'Manrope' }}>
                      Templates Prontos
                    </CardTitle>
                  </div>
                  <p className="text-sm text-amber-600 dark:text-amber-500">Selecione um template para preencher automaticamente</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => applyTemplate(t.id)}
                        className="text-left p-3 rounded-lg border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                        data-testid={`template-${t.id}`}
                      >
                        <p className="font-medium text-sm text-slate-900 dark:text-white">{t.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.items?.length || 0} itens - {formatCurrency(t.total_cost)}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main Product - First Item */}
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-4 border-b border-blue-100">
                <CardTitle className="text-lg text-blue-800" style={{ fontFamily: 'Manrope' }}>
                  Produto Principal (Valor de Compra)
                </CardTitle>
                <p className="text-sm text-blue-600">
                  Este valor será vinculado ao campo "Valor de Compra" na precificação
                </p>
              </CardHeader>
              <CardContent className="pt-4">
                {formData.items.length > 0 && (
                  <div className="flex flex-col lg:flex-row lg:items-end gap-3 p-4 bg-blue-100/50 rounded-lg border border-blue-200">
                    {/* Product name */}
                    <div className="flex-1 min-w-[180px]">
                      <Label className="text-xs text-blue-700 font-medium mb-1 block">Produto Principal *</Label>
                      <div className="flex gap-1">
                        <Input
                          value={formData.items[0]?.name || ""}
                          onChange={(e) => handleItemChange(0, "name", e.target.value)}
                          placeholder="Ex: Fio Garra"
                          className="editable-field flex-1 bg-white"
                          data-testid="input-main-product-name"
                        />
                        {products.length > 0 && (
                          <Select
                            value={formData.items[0]?.product_id || ""}
                            onValueChange={(v) => handleProductSelect(0, v)}
                          >
                            <SelectTrigger className="w-9 px-2 bg-white shrink-0" title="Selecionar produto">
                              <Package className="w-4 h-4 text-blue-500" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name} ({formatCurrency(product.unit_cost)}/{product.unit})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    
                    {/* Quantity */}
                    <div className="w-full lg:w-24">
                      <Label className="text-xs text-blue-700 font-medium mb-1 block">Qtd *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.items[0]?.quantity || ""}
                        onChange={(e) => handleItemChange(0, "quantity", e.target.value)}
                        placeholder="0"
                        className="editable-field font-mono bg-white text-center"
                        data-testid="input-main-product-qty"
                      />
                    </div>
                    
                    {/* Unit */}
                    <div className="w-full lg:w-28">
                      <Label className="text-xs text-blue-700 font-medium mb-1 block">Unidade</Label>
                      <Select
                        value={formData.items[0]?.unit || "un"}
                        onValueChange={(v) => handleItemChange(0, "unit", v)}
                      >
                        <SelectTrigger className="editable-field bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Unit Cost */}
                    <div className="w-full lg:w-28">
                      <Label className="text-xs text-blue-700 font-medium mb-1 block">Custo Unit.</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.items[0]?.unit_cost || ""}
                        onChange={(e) => handleItemChange(0, "unit_cost", e.target.value)}
                        placeholder="0,00"
                        className="editable-field font-mono bg-white text-center"
                        data-testid="input-main-product-cost"
                      />
                    </div>
                    
                    {/* Total */}
                    <div className="w-full lg:w-28 flex items-center justify-between lg:justify-end lg:items-end">
                      <Label className="lg:hidden text-xs text-blue-700 font-medium">Total</Label>
                      <div className="text-right">
                        <Label className="hidden lg:block text-xs text-blue-700 font-medium mb-1">Total</Label>
                        <p className="font-mono font-bold text-blue-800 text-lg whitespace-nowrap">
                          {formatCurrency(costs.mainProduct)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Supplies List - Remaining Items */}
            <Card>
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                      Insumos (Materiais Auxiliares)
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      Este valor será vinculado ao campo "Insumos" na precificação
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddItem}
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Insumo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {/* Header row - Desktop only */}
                  <div className="hidden lg:flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider px-3 pb-2 border-b">
                    <div className="flex-1 min-w-[180px]">Insumo</div>
                    <div className="w-20 text-center">Qtd</div>
                    <div className="w-24 text-center">Unidade</div>
                    <div className="w-24 text-center">Custo Unit.</div>
                    <div className="w-24 text-right">Total</div>
                    <div className="w-10"></div>
                  </div>

                  {/* Item rows (skip first item - main product) */}
                  {formData.items.slice(1).map((item, idx) => {
                    const index = idx + 1; // Real index in array
                    const itemTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
                    
                    return (
                      <div
                        key={index}
                        className="flex flex-col lg:flex-row lg:items-center gap-2 p-3 bg-slate-50 rounded-lg"
                        data-testid={`item-row-${index}`}
                      >
                        {/* Product name */}
                        <div className="flex-1 min-w-[180px]">
                          <Label className="lg:hidden text-xs text-slate-500 mb-1 block">Insumo</Label>
                          <div className="flex gap-1">
                            <Input
                              value={item.name}
                              onChange={(e) => handleItemChange(index, "name", e.target.value)}
                              placeholder="Nome do insumo"
                              className="editable-field flex-1"
                              data-testid={`input-item-name-${index}`}
                            />
                            {products.length > 0 && (
                              <Select
                                value={item.product_id || ""}
                                onValueChange={(v) => handleProductSelect(index, v)}
                              >
                                <SelectTrigger className="w-9 px-2 shrink-0" title="Selecionar produto">
                                  <Package className="w-4 h-4 text-slate-400" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((product) => (
                                    <SelectItem key={product.id} value={product.id}>
                                      {product.name} ({formatCurrency(product.unit_cost)}/{product.unit})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="w-full lg:w-24">
                          <Label className="lg:hidden text-xs text-slate-500 mb-1 block">Quantidade</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.quantity || ""}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            placeholder="0"
                            className="editable-field font-mono text-center"
                            data-testid={`input-item-qty-${index}`}
                          />
                        </div>

                        {/* Unit */}
                        <div className="w-full lg:w-24">
                          <Label className="lg:hidden text-xs text-slate-500 mb-1 block">Unidade</Label>
                          <Select
                            value={item.unit}
                            onValueChange={(v) => handleItemChange(index, "unit", v)}
                          >
                            <SelectTrigger className="editable-field" data-testid={`input-item-unit-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {UNITS.map((unit) => (
                                <SelectItem key={unit.value} value={unit.value}>
                                  {unit.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Unit Cost */}
                        <div className="w-full lg:w-24">
                          <Label className="lg:hidden text-xs text-slate-500 mb-1 block">Custo Unit.</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_cost || ""}
                            onChange={(e) => handleItemChange(index, "unit_cost", e.target.value)}
                            placeholder="0,00"
                            className="editable-field font-mono text-center"
                            data-testid={`input-item-cost-${index}`}
                          />
                        </div>

                        {/* Total */}
                        <div className="w-full lg:w-24 flex items-center justify-between lg:justify-end">
                          <Label className="lg:hidden text-xs text-slate-500">Total</Label>
                          <span className="font-mono font-medium text-slate-700 whitespace-nowrap">
                            {formatCurrency(itemTotal)}
                          </span>
                        </div>

                        {/* Delete */}
                        <div className="w-full lg:w-10 flex items-center justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add more button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed"
                    onClick={handleAddItem}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar mais insumos
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            <div className="lg:sticky lg:top-6">
              <Card className="border-2 border-teal-200 bg-gradient-to-b from-teal-50 to-white">
                <CardHeader className="pb-4 border-b border-teal-100">
                  <CardTitle className="text-lg text-teal-800" style={{ fontFamily: 'Manrope' }}>
                    Resumo da Ficha Técnica
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Main Product Cost */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Valor de Compra</p>
                    <p className="text-xl font-bold font-mono text-blue-800" data-testid="main-product-cost">
                      {formatCurrency(costs.mainProduct)}
                    </p>
                    <p className="text-xs text-blue-500 mt-1">→ Campo "Valor de Compra"</p>
                  </div>

                  {/* Supplies Cost */}
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 font-medium uppercase tracking-wider">Insumos ({formData.items.length - 1} itens)</p>
                    <p className="text-xl font-bold font-mono text-slate-800" data-testid="supplies-cost">
                      {formatCurrency(costs.supplies)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">→ Campo "Insumos"</p>
                  </div>

                  {/* Total */}
                  <div className="p-4 bg-teal-100 rounded-lg border border-teal-200">
                    <p className="text-sm text-teal-700 font-medium">Custo Total</p>
                    <p className="text-3xl font-bold font-mono text-teal-800" data-testid="total-cost">
                      {formatCurrency(costs.total)}
                    </p>
                  </div>

                  <p className="text-xs text-slate-500">
                    Ao vincular esta ficha na precificação, os valores serão preenchidos automaticamente.
                  </p>

                  {/* Save Button */}
                  <Button
                    type="submit"
                    className="w-full bg-teal-600 hover:bg-teal-700 h-12"
                    disabled={saving}
                    data-testid="save-technical-sheet-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Salvando..." : isEditing ? "Atualizar Ficha" : "Criar Ficha"}
                  </Button>

                  {/* Export buttons - only show when editing */}
                  {isEditing && (
                    <div className="pt-4 border-t border-slate-200 space-y-2">
                      <p className="text-xs text-slate-500 font-medium">Exportar</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.open(`${API}/technical-sheets/${id}/export-excel`, '_blank')}
                          data-testid="export-excel-btn"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Excel
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.print()}
                          data-testid="export-pdf-btn"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
